import { aliasFromHost } from "../services/httpClient";
import React, { useState } from "react";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import ContactForm from "../components/ContactForm";
import { useAuth } from "../contexts/AuthContext";
import { passwordSetupService } from "../services/passwordSetupService";
import { isReservedPathSegment } from "../utils/tenantAlias";
import {
  LocalHospital,
  CalendarToday,
  BarChart,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Assignment
} from '@mui/icons-material';
import {
  TextField,
  Button,
  Box,
  Container,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';

const ALIAS_NOT_FOUND = "Clínica não encontrada. Confira o endereço com o administrador do sistema.";

// Mesmo visual dos campos de e-mail e senha do formulário
const loginFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: '45px',
    fontSize: '1rem',
    backgroundColor: 'white',
    '& fieldset': { borderColor: '#ced4da' },
    '&:hover fieldset': { borderColor: '#ced4da' },
    '&.Mui-focused fieldset': {
      borderColor: '#03B4C6',
      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
    },
  },
  '& .MuiOutlinedInput-input': {
    padding: '10.5px 14px',
    color: '#495057',
    '&::placeholder': { color: '#6c757d', opacity: 1 },
  },
};

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactPreSelectedSubject, setContactPreSelectedSubject] = useState<string>();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [isValidClinic, setIsValidClinic] = useState<boolean>(false);
  const [isUserLoggedIn] = useState<boolean>(false);
  // Alias da clínica na tela (pedido do Hilton, 29/08): sem alias na URL o campo é editável e
  // redireciona para /<alias>; com alias válido aparece preenchido e travado.
  const [clinicAlias, setClinicAlias] = useState<string>("");
  const [aliasInput, setAliasInput] = useState<string>("");
  const [aliasError, setAliasError] = useState<string>("");
  const [aliasChecking, setAliasChecking] = useState<boolean>(false);

  React.useEffect(() => {
    document.title = "Clinic4Us - Login";

    // Alias do tenant, por ordem de explicitude (RN-001c): SUBDOMINIO (ninho.clinic4us.com.br)
    // -> sessionStorage (URL curta /<alias>) -> ?clinic= (compatibilidade).
    // O subdominio vem primeiro porque quem digitou o endereco da clinica ja disse em qual quer
    // entrar; sessao antiga de OUTRA clinica nao pode ganhar dele.
    const hostAlias = aliasFromHost();
    const storedAlias = sessionStorage.getItem('clinic4us-clinic-alias');
    const urlParams = new URLSearchParams(window.location.search);
    let clinicParam = (hostAlias || storedAlias || urlParams.get('clinic') || '').trim();
    if (hostAlias && storedAlias && hostAlias !== storedAlias) {
      // Trocou de clinica pelo endereco: a marca da anterior tem de sair, senao a proxima
      // requisicao sai com o alias errado.
      sessionStorage.setItem('clinic4us-clinic-alias', hostAlias);
    }
    // /login (nome de página, não alias) não pode virar "clínica" no campo
    if (isReservedPathSegment(clinicParam)) {
      sessionStorage.removeItem('clinic4us-clinic-alias');
      clinicParam = '';
    }
    setClinicAlias(clinicParam);
    setAliasInput(clinicParam);

    if (!clinicParam) {
      setIsValidClinic(false);
      return;
    }

    // Valida o alias contra a base (MainCustomers/by-alias): existe -> libera o login;
    // não existe -> o campo volta a ser editável, com o erro.
    const validateClinic = async () => {
      setAliasChecking(true);
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5110/api';
        const response = await fetch(`${apiBase}/MainCustomers/by-alias/${encodeURIComponent(clinicParam)}`);

        if (!response.ok) {
          setIsValidClinic(false);
          setAliasError(ALIAS_NOT_FOUND);
          return;
        }

        setIsValidClinic(true);
        setAliasError("");
        // Campos de e-mail e senha iniciam vazios para digitação (sem auto-preenchimento).
      } catch {
        setIsValidClinic(false);
        setAliasError(ALIAS_NOT_FOUND);
      } finally {
        setAliasChecking(false);
      }
    };

    validateClinic();
  }, []);

  const handleAliasContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const alias = aliasInput.trim();
    if (!alias) {
      setAliasError("Informe o endereço da sua clínica.");
      return;
    }

    setAliasChecking(true);
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5110/api';
      const response = await fetch(`${apiBase}/MainCustomers/by-alias/${encodeURIComponent(alias)}`);
      if (!response.ok) {
        setAliasError(ALIAS_NOT_FOUND);
        return;
      }

      // URL curta /<alias>: o roteador guarda o alias e reabre o login já vinculado à clínica
      sessionStorage.setItem('clinic4us-clinic-alias', alias);
      window.location.assign(`/${encodeURIComponent(alias)}`);
    } catch {
      setAliasError("Não foi possível validar a clínica agora. Tente novamente.");
    } finally {
      setAliasChecking(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear errors when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.email) {
      newErrors.email = "Email é obrigatório";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.password) {
      newErrors.password = "Senha é obrigatória";
    } else if (formData.password.length < 6) {
      newErrors.password = "Senha deve ter pelo menos 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Alias do tenant: persistido no sessionStorage (URL curta /<alias>), com fallback para ?clinic=.
      const clinic = sessionStorage.getItem('clinic4us-clinic-alias')
        || new URLSearchParams(window.location.search).get('clinic')
        || '';

      // Fazer login usando o AuthContext (consulta a base do tenant via API)
      const result = await login(formData.email, formData.password, formData.rememberMe, clinic);

      if (result.success) {
        // Conta com senha padrão precisa trocar antes de usar o sistema.
        const destination = result.mustChangePassword
          ? "/?page=change-password"
          : "/?page=dashboard";
        window.location.href = window.location.origin + destination;
      } else {
        setErrors({
          general: result.error || "Credenciais inválidas. Tente novamente.",
        });
      }
    } catch (error) {
      setErrors({
        general: "Erro no servidor. Tente novamente mais tarde.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setErrors({});
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordSent(false);
    setErrors({});
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      setErrors({ email: "Email é obrigatório" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotPasswordEmail)) {
      setErrors({ email: "Email inválido" });
      return;
    }

    const clinic = sessionStorage.getItem('clinic4us-clinic-alias')
      || new URLSearchParams(window.location.search).get('clinic')
      || '';

    setErrors({});
    // Confirmação otimista: a resposta é sempre 204 (não revela se o email existe),
    // então a tela mostra a mesma mensagem neutra em qualquer caso — inclusive falha
    // de rede, para não abrir um canal de descoberta de contas.
    try {
      await passwordSetupService.forgotPassword(clinic, forgotPasswordEmail);
    } catch {
      // silenciado de propósito — ver comentário acima
    }
    setForgotPasswordSent(true);
  };

  const handleForgotPasswordEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForgotPasswordEmail(e.target.value);
    if (errors.email) {
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  };

  const handleGoToPlans = () => {
    // Redirect to landing page plans section
    window.location.href = window.location.origin + '#planos';
  };

  const handleGoHome = () => {
    // Redirect to landing page
    window.location.href = window.location.origin;
  };

  // Contact modal functions
  const openContactModal = (e: React.MouseEvent, preSelectedSubject?: string) => {
    e.preventDefault();
    setContactPreSelectedSubject(preSelectedSubject);
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
    setContactPreSelectedSubject(undefined);
  };

  // Header logged in callbacks
  const handleNotificationClick = () => {
    alert("Você tem 27 notificações!");
  };

  const handleUserClick = () => {
    alert("Menu do usuário - perfil, configurações, logout");
  };

  // Header menu items for login page
  // Assinatura/planos fora do ar até o produto ser aberto a outras clínicas (Hilton, 04/09/2026)
  const SHOW_SUBSCRIPTION = false;

  const loginMenuItems = [
    { label: "Início", onClick: handleGoHome },
    { label: "Contato", onClick: openContactModal },
  ];

  return (
    <div className="login-page login-page-specific">
      {/* Menu sanduíche e "Assinar" OCULTOS (decisão do Hilton, 04/09/2026, véspera da abertura):
          o produto ainda não é oferecido a outras clínicas; voltam quando a landing voltar. */}
      <HeaderInternal
        menuItems={loginMenuItems}
        showCTAButton={SHOW_SUBSCRIPTION && !isUserLoggedIn}
        hideNavigation={!SHOW_SUBSCRIPTION}
        ctaButtonText="Assinar"
        onCTAClick={handleGoToPlans}
        className="login-header"
        isLoggedIn={isUserLoggedIn}
        userEmail={formData.email}
        notificationCount={27}
        onNotificationClick={handleNotificationClick}
        onUserClick={handleUserClick}
      />

      <main className="login-main">
        <Box className="login-container">
          <Box className="login-content">
            <Paper
              className="login-card"
              elevation={0}
              sx={{
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e9ecef',
              }}
            >
              <Box className="login-card-inner">
                <Box className="login-image-section">
                </Box>

                <Box className="login-form-section">
                  <Box className="login-header-content">
                    <Typography variant="body1">
                      {showForgotPassword
                        ? "Recuperar senha"
                        : isValidClinic
                          ? "Acesse sua conta para gerenciar sua clínica"
                          : "Informe o endereço da sua clínica para continuar"}
                    </Typography>
                  </Box>

                  {!showForgotPassword && (
                    // mesmo espaçamento do gap do .login-form, para as linhas ficarem equidistantes
                    <Box component="form" onSubmit={handleAliasContinue} sx={{ mb: '0.875rem' }}>
                      <TextField
                        id="clinic-alias"
                        name="clinicAlias"
                        label="Clínica"
                        value={isValidClinic ? clinicAlias : aliasInput}
                        onChange={(e) => {
                          setAliasInput(e.target.value);
                          if (aliasError) setAliasError("");
                        }}
                        placeholder="endereço da clínica (ex.: ninho)"
                        disabled={isValidClinic || aliasChecking}
                        error={!!aliasError}
                        helperText={aliasError || (isValidClinic ? undefined : "É o nome que vem logo depois de clinic4us.com.br/ no link da sua clínica")}
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxLength: 60, autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
                        sx={loginFieldSx}
                      />
                      {!isValidClinic && (
                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          disabled={aliasChecking}
                          sx={{
                            mt: 2,
                            height: '45px',
                            backgroundColor: '#03B4C6',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: '#029AAB' },
                          }}
                        >
                          {aliasChecking ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Continuar'}
                        </Button>
                      )}
                    </Box>
                  )}

                  {!isValidClinic ? null : !showForgotPassword ? (
                    <>
                      <Box component="form" onSubmit={handleSubmit} className="login-form">
                        {errors.general && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.general}
                          </Alert>
                        )}

                        <Box>
                          <TextField
                            type="email"
                            id="email"
                            name="email"
                            label="Email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="seu@email.com"
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={isLoading}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '45px',
                                fontSize: '1rem',
                                backgroundColor: 'white',
                                '& fieldset': {
                                  borderColor: '#ced4da',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#ced4da',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#03B4C6',
                                  boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '10.5px 14px',
                                color: '#495057',
                                '&::placeholder': {
                                  color: '#6c757d',
                                  opacity: 1,
                                },
                              },
                            }}
                          />
                        </Box>

                        <Box>
                          <TextField
                            type={showPassword ? "text" : "password"}
                            id="password"
                            name="password"
                            label="Senha"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Digite sua senha"
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={isLoading}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '45px',
                                fontSize: '1rem',
                                backgroundColor: 'white',
                                '& fieldset': {
                                  borderColor: '#ced4da',
                                },
                                '&:hover fieldset': {
                                  borderColor: '#ced4da',
                                },
                                '&.Mui-focused fieldset': {
                                  borderColor: '#03B4C6',
                                  boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                                },
                              },
                              '& .MuiOutlinedInput-input': {
                                padding: '10.5px 14px',
                                paddingRight: '3rem',
                                color: '#495057',
                                '&::placeholder': {
                                  color: '#6c757d',
                                  opacity: 1,
                                },
                              },
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={isLoading}
                                    edge="end"
                                    sx={{ color: '#6c757d' }}
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>

                        {/* "Lembrar de mim" saiu (29/08/2026): só gravava uma flag e não estendia a sessão —
                            prometia guardar o acesso sem guardar (RN-006b) */}
                        <Box className="form-options" sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
                          <Button
                            type="button"
                            variant="text"
                            onClick={handleForgotPassword}
                            disabled={isLoading}
                            sx={{
                              textTransform: 'none',
                              color: '#03B4C6',
                              fontSize: '1rem',
                              '&:hover': {
                                backgroundColor: 'rgba(3, 180, 198, 0.04)',
                              },
                            }}
                          >
                            Esqueceu a senha?
                          </Button>
                        </Box>

                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          disabled={isLoading}
                          sx={{
                            py: 1.5,
                            mb: 2,
                            backgroundColor: '#03B4C6',
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: '#029AAB',
                              boxShadow: 'none',
                            },
                            '&:disabled': {
                              backgroundColor: '#6c757d',
                              color: 'white',
                            },
                          }}
                        >
                          {isLoading ? (
                            <>
                              <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                              Entrando...
                            </>
                          ) : (
                            "Entrar"
                          )}
                        </Button>
                      </Box>

                      {SHOW_SUBSCRIPTION && (
                      <Box className="login-footer" sx={{ textAlign: 'center', mt: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '1rem', color: '#6c757d' }}>
                          Não tem uma conta?{" "}
                          <Button
                            variant="text"
                            onClick={handleGoToPlans}
                            disabled={isLoading}
                            sx={{
                              textTransform: 'none',
                              p: 0,
                              minWidth: 'auto',
                              fontSize: '1rem',
                              color: '#03B4C6',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            Assinar
                          </Button>
                        </Typography>
                      </Box>
                      )}

                    </>
                  ) : (
                    // Formulário de Esqueci a Senha
                    <Box className="forgot-password-form">
                      {!forgotPasswordSent ? (
                        <Box component="form" onSubmit={handleForgotPasswordSubmit} className="login-form">
                          <Box sx={{ mb: 3 }}>
                            <TextField
                              type="email"
                              id="forgotEmail"
                              name="forgotEmail"
                              label="Email"
                              value={forgotPasswordEmail}
                              onChange={handleForgotPasswordEmailChange}
                              placeholder="Digite seu email"
                              error={!!errors.email}
                              helperText={errors.email}
                              required
                              fullWidth
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '45px',
                                  fontSize: '1rem',
                                  backgroundColor: 'white',
                                  '& fieldset': {
                                    borderColor: '#ced4da',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#ced4da',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#03B4C6',
                                    boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '10.5px 14px',
                                  color: '#495057',
                                  '&::placeholder': {
                                    color: '#6c757d',
                                    opacity: 1,
                                  },
                                },
                              }}
                            />
                          </Box>

                          <Box className="form-actions" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              fullWidth
                              sx={{
                                py: 1.5,
                                backgroundColor: '#03B4C6',
                                fontSize: '1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '8px',
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: '#029AAB',
                                  boxShadow: 'none',
                                },
                              }}
                            >
                              Enviar Link de Recuperação
                            </Button>
                            <Button
                              type="button"
                              variant="text"
                              onClick={handleBackToLogin}
                              fullWidth
                              sx={{
                                textTransform: 'none',
                                color: '#03B4C6',
                                fontSize: '1rem',
                                '&:hover': {
                                  backgroundColor: 'rgba(3, 180, 198, 0.04)',
                                },
                              }}
                            >
                              ← Voltar ao Login
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Box className="success-icon" sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
                          </Box>
                          <Typography variant="h6" sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600, color: '#212529' }}>Verifique seu email</Typography>
                          <Typography variant="body1" sx={{ mb: 3, fontSize: '1rem', color: '#6c757d' }}>
                            Se <strong style={{ color: '#212529' }}>{forgotPasswordEmail}</strong> estiver cadastrado,
                            você receberá um link para redefinir a senha. Verifique sua caixa de entrada e o spam.
                          </Typography>
                          <Button
                            variant="text"
                            onClick={handleBackToLogin}
                            fullWidth
                            sx={{
                              textTransform: 'none',
                              color: '#03B4C6',
                              fontSize: '1rem',
                              '&:hover': {
                                backgroundColor: 'rgba(3, 180, 198, 0.04)',
                              },
                            }}
                          >
                            ← Voltar ao Login
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </main>

      {/* Modal de Contato */}
      <ContactForm
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        preSelectedSubject={contactPreSelectedSubject}
      />

      <FooterInternal
        className="login-footer-component"
      />
    </div>
  );
};

export default Login;
